/**
 * 语音音频管线（S2 客户端）
 *
 * 采集：getUserMedia → AudioContext(24kHz) → AudioWorklet → PCM16 块回调
 * 播放：PCM16 base64 块 → Float32 → AudioBuffer 顺序拼接播放
 *
 * 采样率兜底：AudioContext(24000) 不支持的浏览器回退默认采样率 + 线性重采样。
 * 隐私（V-UI-10）：stopCapture 释放麦克风轨道，系统麦克风指示灯熄灭为证。
 */

const TARGET_RATE = 24000;

const WORKLET_SRC = `
class PCMCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) {
      const int16 = new Int16Array(ch.length);
      for (let i = 0; i < ch.length; i++) {
        const s = Math.max(-1, Math.min(1, ch[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      this.port.postMessage(int16.buffer, [int16.buffer]);
    }
    return true;
  }
}
registerProcessor('pcm-capture', PCMCapture);
`;

/** 线性重采样 Float32（from → to 采样率）。 */
function resampleFloat32(input, fromRate, toRate) {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.round(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = input[idx] || 0;
    const b = input[idx + 1] || a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

function secureContextMicError() {
  const err = new Error('Microphone requires a secure context (HTTPS or localhost)');
  err.name = 'NotSecureContextError';
  return err;
}

export class VoiceAudio {
  constructor({ onChunk, onPlaybackLevel } = {}) {
    this.onChunk = onChunk;           // (base64 PCM16 24k)
    this.onPlaybackLevel = onPlaybackLevel; // 播放能量（驱动说话动效）
    this._ctx = null;
    this._stream = null;
    this._workletNode = null;
    this._source = null;
    this._playCtx = null;
    this._playTime = 0;
    this._capturing = false;
  }

  /** 采集开始。权限被拒抛 DOMException(NotAllowedError)，由上层优雅引导（V-EDGE-07）。 */
  async startCapture() {
    if (this._capturing) return;
    if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw secureContextMicError();
    }
    this._stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    let ctx;
    try { ctx = new AudioContext({ sampleRate: TARGET_RATE }); }
    catch (e) { ctx = new AudioContext(); } // 采样率兜底
    this._ctx = ctx;
    const nativeRate = ctx.sampleRate;

    const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    try { await ctx.audioWorklet.addModule(url); }
    finally { URL.revokeObjectURL(url); }

    this._source = ctx.createMediaStreamSource(this._stream);
    this._workletNode = new AudioWorkletNode(ctx, 'pcm-capture');
    this._workletNode.port.onmessage = (ev) => {
      if (!this.onChunk) return;
      let int16 = new Int16Array(ev.data);
      if (nativeRate !== TARGET_RATE) {
        const f = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) f[i] = int16[i] / 0x8000;
        const rs = resampleFloat32(f, nativeRate, TARGET_RATE);
        int16 = new Int16Array(rs.length);
        for (let i = 0; i < rs.length; i++) int16[i] = Math.max(-32768, Math.min(32767, Math.round(rs[i] * 0x8000)));
      }
      // base64 编码（分块防栈溢出）
      const u8 = new Uint8Array(int16.buffer);
      let bin = '';
      for (let i = 0; i < u8.length; i += 8192) {
        bin += String.fromCharCode.apply(null, u8.subarray(i, i + 8192));
      }
      this.onChunk(btoa(bin));
    };
    this._source.connect(this._workletNode);
    this._workletNode.connect(ctx.destination); // 某些浏览器需要输出链路才驱动 process
    this._capturing = true;
  }

  /** 采集停止并释放麦克风（挂起/关闭时调用，麦克风指示灯熄灭）。 */
  stopCapture() {
    this._capturing = false;
    try { this._workletNode && this._workletNode.disconnect(); } catch (e) { /* */ }
    try { this._source && this._source.disconnect(); } catch (e) { /* */ }
    if (this._stream) {
      for (const track of this._stream.getTracks()) track.stop();
      this._stream = null;
    }
    if (this._ctx) { try { this._ctx.close(); } catch (e) { /* */ } this._ctx = null; }
    this._workletNode = null;
    this._source = null;
  }

  /** 播放一块 PCM16 base64（顺序拼接，无缝）。 */
  playChunk(base64) {
    try {
      if (!this._playCtx) {
        try { this._playCtx = new AudioContext({ sampleRate: TARGET_RATE }); }
        catch (e) { this._playCtx = new AudioContext(); }
        this._playTime = this._playCtx.currentTime;
      }
      const bin = atob(base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const int16 = new Int16Array(u8.buffer);
      let f32 = new Float32Array(int16.length);
      let peak = 0;
      for (let i = 0; i < int16.length; i++) {
        f32[i] = int16[i] / 0x8000;
        const a = Math.abs(f32[i]);
        if (a > peak) peak = a;
      }
      if (this._playCtx.sampleRate !== TARGET_RATE) {
        f32 = resampleFloat32(f32, TARGET_RATE, this._playCtx.sampleRate);
      }
      const buf = this._playCtx.createBuffer(1, f32.length, this._playCtx.sampleRate);
      buf.getChannelData(0).set(f32);
      const src = this._playCtx.createBufferSource();
      src.buffer = buf;
      src.connect(this._playCtx.destination);
      const now = this._playCtx.currentTime;
      const startAt = Math.max(now, this._playTime);
      src.start(startAt);
      this._playTime = startAt + buf.duration;
      if (this.onPlaybackLevel) this.onPlaybackLevel(peak);
    } catch (e) { /* 单块失败不中断会话 */ }
  }

  /** 停止播放并清空队列（静音/结束时）。 */
  stopPlayback() {
    if (this._playCtx) { try { this._playCtx.close(); } catch (e) { /* */ } this._playCtx = null; }
    this._playTime = 0;
  }

  /** 正在播放中（用于"让助理说完当前这句再停"的客户端侧判断）。 */
  isPlaying() {
    return !!(this._playCtx && this._playTime > this._playCtx.currentTime + 0.05);
  }

  destroy() {
    this.stopCapture();
    this.stopPlayback();
  }
}
